import React, { useState, useContext, useEffect } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Button, Typography, Autocomplete, TextField, MenuItem, FormControl, Select, InputLabel } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { AuthContext } from '../AuthContextProvider';
import ErrorDialog from '../ErrorDialog';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { rpc } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import '../App.css';

const ScheduledEvent = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [repeat, setRepeat] = useState('0');
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  let { id } = useParams();

  moment.tz.setDefault(user._site.timezone);

  useEffect(() => {
    const onFetchSuccess = (scheduledEvent) => {
      setSelectedDate(moment(scheduledEvent.shownOn));
      setSelectedEvent(scheduledEvent._event);
      setSelectedGroups(scheduledEvent._eventGroups);
    };

    const fetchScheduledEvent = async (id) => {
      const params = {
        _id: id,
      };
      rpc({
        method: 'GetScheduledEvent',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: onFetchSuccess
      });
    };

    const getEvents = async () => {
      const params = {
        _site: user._site._id,
      };
      rpc({
        method: 'GetEvents',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setEvents
      });
    };

    const getGroups = async () => {
      const params = {
        _site: user._site._id,
      };
      rpc({
        method: 'GetEventGroups',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setGroups
      });
    };

    getEvents();
    getGroups();
    if (id) {
      fetchScheduledEvent(id);
    }
  }, [id, user]);


  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const onSuccess = () => {
    navigate('/scheduledEvents');
  };

  const createScheduledEvent = async () => {
    if (selectedDate === null || selectedEvent === null || selectedGroups.length === 0) {
      setErrorMessage('Date, Event and Group fields are required');
      setOpenErrorDialog(true);
      return;
    }
    const eventGroups = selectedGroups.map((group) => group._id);
    const params = {
      _site: user._site._id,
      shownOn: selectedDate,
      _event: selectedEvent._id,
      _eventGroups: eventGroups,
      repeat
    };
    rpc({
      method: 'CreateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const updateScheduledEvent = async () => {
    if (selectedDate === null || selectedEvent === null || selectedGroups.length === 0) {
      setErrorMessage('Date, Event and Group fields are required');
      setOpenErrorDialog(true);
      return;
    }
    const eventGroups = selectedGroups.map((group) => group._id);
    const params = {
      _id: id,
      shownOn: selectedDate,
      _event: selectedEvent._id,
      _eventGroups: eventGroups,
    };
    rpc({
      method: 'UpdateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const deleteScheduledEvent = async () => {
    const params = {
      _id: id,
    };
    rpc({
      method: 'DeleteScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
  };

  const handleEventChange = (event, value) => {
    setSelectedEvent(value);
  };

  const handleGroupChange = (event, value) => {
    setSelectedGroups(value);
  };

  const handleRepeatChange = (event) => {
    setRepeat(event.target.value);
  };

  const getButtons = () => {
    if (id) {
      return (
        <>
          <div className="HorizontalContainer">
            <Button className="Button" variant="contained" color="primary" onClick={updateScheduledEvent}>
            Update
            </Button>
            <Button className="Button" variant="contained" color="primary" onClick={deleteScheduledEvent}>
            Delete
            </Button>
          </div>
        </>
      );
    } return (
      <>
        <FormControl className="Select" sx={{marginTop: "1em", width: 600}}>
          <InputLabel className="Select" id="repeat-label">Repeat</InputLabel>
          <Select
            className="Select"
            labelId="repeat-label"
            id="repeat"
            value={repeat}
            label="Repeat"
            onChange={handleRepeatChange}
          >
            <MenuItem value="0">Do not repeat</MenuItem>
            <MenuItem value="1">1 week</MenuItem>
            <MenuItem value="2">2 weeks</MenuItem>
            <MenuItem value="3">3 weeks (one month)</MenuItem>
          </Select>
        </FormControl>
        <Button className="Button" variant="contained" color="primary" onClick={createScheduledEvent}>
          Create
        </Button>
      </>
    );
  };

  const getEventLabel = (event) => {
    let label = event.displayName;
    event.attributes.forEach((attribute) => {
      if (attribute.name === Constants.ATTR_PLAY_IMMEDIATELY && attribute.value === 'true') {
        label += ' (immediate)';
      }
    });
    return label;
  };

  return (
    <div className="ScheduledEventDetails">
      <Typography variant="h6" gutterBottom>
        Scheduled Event {selectedEvent ? selectedEvent.displayName : '' }
      </Typography>
      <LocalizationProvider
        adapterLocale="en"
        dateAdapter={AdapterMoment}
        dateLibInstance={moment}
      >
        <DateTimePicker className="DateTimePicker"
          label="Select Date"
          value={selectedDate}
          onChange={handleDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          format={Constants.DATE_TIME_FORMAT}
        />
      </LocalizationProvider>
      <Autocomplete className="Autocomplete"
        value={selectedEvent}
        isOptionEqualToValue={(event, value) => event?._id === value?._id}
        options={events}
        onChange={handleEventChange}
        getOptionLabel={(event) => getEventLabel(event)}
        renderInput={(params) => <TextField {...params} label="Event" variant="outlined" />}
      />
      <Autocomplete className="Autocomplete"
        multiple
        value={selectedGroups}
        isOptionEqualToValue={(group, value) => group?._id === value?._id}
        options={groups}
        onChange={handleGroupChange}
        getOptionLabel={(group) => group.displayName}
        renderInput={(params) => <TextField {...params} label="Groups" variant="outlined" />}
      />
      {getButtons()}
      <ErrorDialog open={openErrorDialog} onErrorDialogClose={handleClose} errorMessage={errorMessage} />
    </div>
  );
};

export default ScheduledEvent;
